import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Shield, ExternalLink } from "lucide-react";
import { get, patch } from "@/lib/api";

const STATUSES = ["pending", "submitted", "reviewed", "approved", "rejected", "waitlist"];
const STATUS_COLOR = {
    pending: "text-white/55 border-[#1a2a4a] bg-[#0a1018]",
    submitted: "text-[#00B7FF] border-[#1E90FF]/40 bg-[#0a1429]",
    reviewed: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#181301]",
    approved: "text-[#22d3ee] border-[#22d3ee]/40 bg-[#05151a]",
    rejected: "text-[#fca5a5] border-[#fca5a5]/40 bg-[#1a0606]",
    waitlist: "text-[#a3b5d1] border-[#1a2a4a] bg-[#0a1018]",
};

const COMMIT_FIELDS = [
    ["commit_show_up", "Show up"],
    ["commit_field_work", "Field work"],
    ["commit_honesty", "Honesty"],
    ["commit_no_quit", "No quit"],
    ["commit_ready_to_start", "Ready to start"],
];

const NARRATIVE_FIELDS = [
    ["why_now", "Why now"],
    ["biggest_fear", "Biggest fear"],
    ["success_vision", "Vision (Day 90)"],
    ["past_attempts", "Past attempts"],
    ["past_attempts_why_failed", "Why they failed"],
    ["time_per_week", "Time / week"],
    ["weekly_schedule", "Weekly schedule"],
    ["financial_explain", "Financial context"],
    ["coachability_explain", "Coachability — explain"],
    ["current_lifestyle", "Current lifestyle"],
    ["dating_status", "Dating status"],
    ["anything_else", "Anything else"],
];

export default function Applications() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [savingId, setSavingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await get(
                filter ? `/admin/applications?status=${filter}` : "/admin/applications"
            );
            setRows(data);
        } catch {
            toast.error("Failed to load applications");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const updateApp = async (id, body) => {
        setSavingId(id);
        try {
            const next = await patch(`/admin/applications/${id}`, body);
            setRows((r) => r.map((x) => (x.id === id ? next : x)));
            if (active?.id === id) setActive(next);
            toast.success("Updated");
        } catch {
            toast.error("Update failed");
        } finally {
            setSavingId(null);
        }
    };

    const counts = useMemo(() => {
        const map = { all: rows.length };
        for (const s of STATUSES) map[s] = 0;
        for (const r of rows) {
            map[r.status] = (map[r.status] || 0) + 1;
        }
        return map;
    }, [rows]);

    return (
        <div data-testid="admin-applications">
            <header className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#00B7FF]">
                    Pipeline
                </p>
                <h1 className="mt-2 font-display font-black text-white text-3xl md:text-4xl tracking-tight">
                    Applications
                </h1>
                <p className="mt-2 text-white/55 text-sm">
                    Post-consultation qualifying applications. Review, score,
                    approve, or reject.
                </p>
            </header>

            <div className="mb-5 flex flex-wrap items-center gap-2">
                <FilterPill
                    label="All"
                    count={counts.all}
                    active={!filter}
                    onClick={() => setFilter("")}
                    testId="filter-all"
                />
                {STATUSES.map((s) => (
                    <FilterPill
                        key={s}
                        label={s}
                        count={counts[s] || 0}
                        active={filter === s}
                        onClick={() => setFilter(s)}
                        testId={`filter-${s}`}
                    />
                ))}
            </div>

            <div className="bg-[#060c1e] border border-[#1a2a4a]">
                <div className="grid grid-cols-12 px-5 py-3 border-b border-[#1a2a4a] text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">
                    <div className="col-span-3">Name</div>
                    <div className="col-span-3 hidden md:block">Contact</div>
                    <div className="col-span-2">Score</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 hidden md:block">Submitted</div>
                </div>

                {loading && (
                    <div className="px-5 py-12 text-white/55 text-sm flex items-center gap-3">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="px-5 py-12 text-white/55 text-sm">
                        No applications yet. Send the apply link from the
                        Prospects tab.
                    </div>
                )}

                {!loading &&
                    rows.map((a) => {
                        const isActive = active?.id === a.id;
                        const color = STATUS_COLOR[a.status] || STATUS_COLOR.submitted;
                        const score = a.admin_score ?? 0;
                        const scoreColor =
                            score >= 75
                                ? "text-[#22d3ee]"
                                : score >= 50
                                ? "text-[#D4AF37]"
                                : "text-[#fca5a5]";
                        return (
                            <div
                                key={a.id}
                                data-testid={`application-row-${a.id}`}
                                className={`border-b border-[#142142] last:border-b-0 transition-colors ${
                                    isActive ? "bg-[#0a1429]" : "hover:bg-[#0a1429]/40"
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setActive(isActive ? null : a)}
                                    className="w-full grid grid-cols-12 px-5 py-4 items-center text-left"
                                >
                                    <div className="col-span-3">
                                        <p className="text-white font-semibold text-sm">
                                            {a.name}
                                        </p>
                                        <p className="text-white/50 text-[11px]">
                                            Coachability {a.coachability_score}/10
                                            · ${a.financial_ready ? "$$$ ready" : "no $ commit"}
                                        </p>
                                    </div>
                                    <div className="col-span-3 hidden md:block">
                                        <p className="text-white/85 text-xs flex items-center gap-1.5">
                                            <Mail size={11} />
                                            {a.email}
                                        </p>
                                        <p className="text-white/55 text-[11px] flex items-center gap-1.5 mt-1">
                                            <Phone size={11} />
                                            {a.phone}
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <p
                                            className={`font-display font-black text-2xl tracking-tight ${scoreColor}`}
                                        >
                                            {score}
                                            <span className="text-white/30 text-xs ml-1">
                                                /100
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <span
                                            className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold border ${color}`}
                                        >
                                            {a.status}
                                        </span>
                                    </div>
                                    <div className="col-span-2 hidden md:block text-white/50 text-[11px]">
                                        {new Date(a.submitted_at).toLocaleDateString()}
                                    </div>
                                </button>

                                {isActive && (
                                    <div className="px-5 pb-6 pt-2">
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                            <div className="lg:col-span-2 space-y-4">
                                                <div className="bg-[#020617] border border-[#1a2a4a] p-4">
                                                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-3">
                                                        Commitments
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {COMMIT_FIELDS.map(
                                                            ([key, label]) => (
                                                                <span
                                                                    key={key}
                                                                    className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold border ${
                                                                        a[key]
                                                                            ? "border-[#1E90FF]/60 text-[#00B7FF] bg-[#05151a]"
                                                                            : "border-[#fca5a5]/40 text-[#fca5a5] bg-[#1a0606]"
                                                                    }`}
                                                                >
                                                                    {a[key] ? "✓" : "✕"}{" "}
                                                                    {label}
                                                                </span>
                                                            )
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="bg-[#020617] border border-[#1a2a4a] p-4 space-y-3 max-h-[420px] overflow-y-auto">
                                                    {NARRATIVE_FIELDS.map(
                                                        ([key, label]) => (
                                                            <div key={key}>
                                                                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF]">
                                                                    {label}
                                                                </p>
                                                                <p className="mt-1 text-white/85 text-sm whitespace-pre-wrap leading-relaxed">
                                                                    {a[key] ||
                                                                        (
                                                                            <span className="text-white/30">
                                                                                —
                                                                            </span>
                                                                        )}
                                                                </p>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-2">
                                                        Status
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {STATUSES.map((s) => (
                                                            <button
                                                                key={s}
                                                                type="button"
                                                                data-testid={`set-app-status-${s}-${a.id}`}
                                                                disabled={
                                                                    savingId === a.id
                                                                }
                                                                onClick={() =>
                                                                    updateApp(
                                                                        a.id,
                                                                        {
                                                                            status: s,
                                                                        }
                                                                    )
                                                                }
                                                                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-bold border transition-all ${
                                                                    a.status === s
                                                                        ? "bg-[#1E90FF] text-white border-[#1E90FF]"
                                                                        : "bg-transparent text-white/65 border-[#1a2a4a] hover:border-[#1E90FF] hover:text-white"
                                                                }`}
                                                            >
                                                                {s}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <ScoreEditor
                                                    app={a}
                                                    onSave={(score) =>
                                                        updateApp(a.id, {
                                                            admin_score: score,
                                                        })
                                                    }
                                                    saving={savingId === a.id}
                                                />

                                                <NotesEditor
                                                    initial={a.admin_notes || ""}
                                                    saving={savingId === a.id}
                                                    onSave={(notes) =>
                                                        updateApp(a.id, {
                                                            admin_notes: notes,
                                                        })
                                                    }
                                                />

                                                <a
                                                    href={`/apply/${a.token}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`view-public-${a.id}`}
                                                    className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#1a2a4a] text-white/70 hover:border-[#1E90FF] hover:text-[#00B7FF]"
                                                >
                                                    <ExternalLink size={11} />{" "}
                                                    Open public view
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

            <div className="mt-6 inline-flex items-center gap-2 text-white/45 text-[11px] uppercase tracking-[0.2em] font-semibold">
                <Shield size={12} className="text-[#00B7FF]" /> Auto-score is a
                heuristic. Use your judgment for the final call.
            </div>
        </div>
    );
}

function FilterPill({ label, count, active, onClick, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-bold border transition-all ${
                active
                    ? "bg-[#1E90FF] text-white border-[#1E90FF]"
                    : "bg-transparent text-white/60 border-[#1a2a4a] hover:border-[#1E90FF] hover:text-white"
            }`}
        >
            {label}{" "}
            <span className="text-white/45 font-normal ml-1">{count}</span>
        </button>
    );
}

function ScoreEditor({ app, onSave, saving }) {
    const [val, setVal] = useState(app.admin_score ?? 0);
    useEffect(() => setVal(app.admin_score ?? 0), [app.admin_score]);
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-2">
                Admin Score
            </p>
            <div className="flex items-center gap-3">
                <input
                    data-testid={`app-score-${app.id}`}
                    type="range"
                    min={0}
                    max={100}
                    value={val}
                    onChange={(e) => setVal(parseInt(e.target.value, 10))}
                    className="flex-1 accent-[#1E90FF]"
                />
                <span className="font-display font-black text-[#00B7FF] text-xl tracking-tight w-10 text-right">
                    {val}
                </span>
            </div>
            <button
                type="button"
                data-testid={`app-score-save-${app.id}`}
                disabled={saving || val === (app.admin_score ?? 0)}
                onClick={() => onSave(val)}
                className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-transparent border border-[#1a2a4a] text-white text-[10px] uppercase tracking-[0.15em] font-bold hover:border-[#1E90FF] hover:text-[#00B7FF] disabled:opacity-50 transition-all"
            >
                {saving ? <Loader2 className="animate-spin" size={11} /> : null}
                Save Score
            </button>
        </div>
    );
}

function NotesEditor({ initial, onSave, saving }) {
    const [val, setVal] = useState(initial);
    useEffect(() => setVal(initial), [initial]);
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00B7FF] mb-2">
                Admin Notes
            </p>
            <textarea
                data-testid="app-admin-notes"
                rows={4}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="Internal call review notes…"
                className="w-full bg-[#020617] border border-[#1a2a4a] focus:border-[#1E90FF] outline-none text-white text-sm px-3 py-2.5 resize-none"
            />
            <button
                type="button"
                data-testid="app-admin-notes-save"
                disabled={saving || val === initial}
                onClick={() => onSave(val)}
                className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-transparent border border-[#1a2a4a] text-white text-[10px] uppercase tracking-[0.15em] font-bold hover:border-[#1E90FF] hover:text-[#00B7FF] disabled:opacity-50 transition-all"
            >
                {saving ? <Loader2 className="animate-spin" size={11} /> : null}
                Save Notes
            </button>
        </div>
    );
}
